import {
  Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { BaseCellEditor } from './base-editor';
import { CELL_EDITOR_STYLES } from './cell-editor.styles';
import { ChildTableA11yService } from '../../services/child-table-a11y.service';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface CalendarDay {
  date: number;
  month: number;
  year: number;
  isCurrentMonth: boolean;
  iso: string;
}

@Component({
  selector: 'ct-date-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [CELL_EDITOR_STYLES, `
    :host { display: none; }

    .ct-dp-backdrop {
      position: fixed; inset: 0; z-index: 10000;
      background: rgba(0,0,0,0.45);
      display: flex; align-items: center; justify-content: center;
    }
    .ct-dp-modal {
      width: min(340px, 90vw); background: var(--og-surface-primary, #fff);
      border-radius: var(--og-radius-lg, 12px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.24);
      display: flex; flex-direction: column; overflow: hidden;
    }

    .ct-dp-modal-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid var(--og-border-default, rgba(0,0,0,0.12));
    }
    .ct-dp-modal-title { font-weight: 600; font-size: 0.9375rem; }
    .ct-dp-modal-close {
      background: none; border: none; cursor: pointer; padding: 4px;
      color: var(--og-text-secondary, #666); border-radius: var(--og-radius-sm, 4px);
    }
    .ct-dp-modal-close:hover { background: var(--og-surface-secondary, #f5f5f5); }

    .ct-dp-body { padding: 12px 16px; }

    .ct-dp-header {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 8px;
    }
    .ct-dp-header button {
      display: flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border: none; border-radius: 4px;
      background: transparent; cursor: pointer;
      color: var(--og-text-primary, rgba(0,0,0,0.87));
    }
    .ct-dp-header button:hover { background: var(--og-bg-hover, rgba(0,0,0,0.04)); }
    .ct-dp-header button:focus-visible {
      outline: 2px solid var(--og-primary, #4b3fff); outline-offset: -2px;
    }
    .ct-dp-title {
      font-weight: 600; font-size: 14px;
      color: var(--og-text-primary, rgba(0,0,0,0.87));
    }

    .ct-dp-weekdays {
      display: grid; grid-template-columns: repeat(7, 1fr); gap: 0; margin-bottom: 4px;
    }
    .ct-dp-weekday {
      text-align: center; font-size: 11px; font-weight: 600;
      color: var(--og-text-disabled, rgba(0,0,0,0.54)); padding: 4px 0; user-select: none;
    }

    .ct-dp-days { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
    .ct-dp-day {
      display: flex; align-items: center; justify-content: center;
      width: 100%; aspect-ratio: 1; border: none; border-radius: 50%;
      background: transparent; cursor: pointer;
      font-size: 13px; color: var(--og-text-primary, rgba(0,0,0,0.87)); transition: background 0.1s;
    }
    .ct-dp-day:hover { background: var(--og-bg-hover, rgba(0,0,0,0.04)); }
    .ct-dp-day.other-month { color: var(--og-text-disabled, rgba(0,0,0,0.38)); }
    .ct-dp-day.today {
      font-weight: 700; outline: 1px solid var(--og-border-default, rgba(0,0,0,0.31)); outline-offset: -1px;
    }
    .ct-dp-day.selected { background: var(--og-primary, #4b3fff); color: #fff; font-weight: 600; }
    .ct-dp-day.selected:hover { background: var(--og-primary-dark, #19009b); }
    .ct-dp-day.focused {
      box-shadow: inset 0 0 0 2px var(--og-primary, #4b3fff);
      background: rgba(75, 63, 255, 0.08);
    }
    .ct-dp-day:focus-visible {
      box-shadow: inset 0 0 0 2px var(--og-primary, #4b3fff);
      background: rgba(75, 63, 255, 0.08);
      outline: none;
    }
    .ct-dp-day.selected.focused,
    .ct-dp-day.selected:focus-visible {
      background: var(--og-primary, #4b3fff);
      color: #fff;
      box-shadow: 0 0 0 3px rgba(75, 63, 255, 0.3);
      outline: none;
    }

    .ct-dp-time {
      display: flex; align-items: center; gap: 6px;
      margin-top: 8px; padding-top: 8px;
      border-top: 1px solid var(--og-border-light, rgba(0,0,0,0.12));
    }
    .ct-dp-time label { font-size: 12px; color: var(--og-text-secondary, rgba(0,0,0,0.6)); }
    .ct-dp-time input {
      width: 48px; padding: 4px 6px; border: 1px solid var(--og-border-default, rgba(0,0,0,0.31));
      border-radius: 4px; font-size: 13px; text-align: center;
      color: var(--og-text-primary, rgba(0,0,0,0.87));
    }
    .ct-dp-time input:focus {
      outline: 2px solid var(--og-primary, #4b3fff); outline-offset: -2px; border-color: transparent;
    }
    .ct-dp-time-sep { font-size: 14px; font-weight: 600; color: var(--og-text-secondary, rgba(0,0,0,0.6)); }

    .ct-dp-footer {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 16px;
      border-top: 1px solid var(--og-border-default, rgba(0,0,0,0.12));
    }
    .ct-dp-footer button {
      padding: 4px 12px; border: none; border-radius: 4px;
      font-size: 12px; font-weight: 500; cursor: pointer;
    }
    .ct-dp-footer button:focus-visible {
      outline: 2px solid var(--og-primary, #4b3fff); outline-offset: 2px;
    }
    .ct-dp-btn-clear {
      background: transparent; color: var(--og-text-secondary, rgba(0,0,0,0.6));
    }
    .ct-dp-btn-clear:hover { background: var(--og-bg-hover, rgba(0,0,0,0.04)); }
    .ct-dp-btn-today {
      background: transparent; color: var(--og-primary, #4b3fff);
    }
    .ct-dp-btn-today:hover { background: rgba(75,63,255,0.08); }
    .ct-dp-spacer { flex: 1; }
    .ct-dp-btn-cancel {
      background: var(--og-surface-primary, #fff); color: var(--og-text-primary, #1a1a1a);
      border: 1px solid var(--og-border-default, rgba(0,0,0,0.2)) !important;
      padding: 5px 14px; border-radius: var(--og-radius-sm, 6px);
    }
    .ct-dp-btn-cancel:hover { background: var(--og-surface-secondary, #f5f5f5); }
    .ct-dp-btn-save {
      background: var(--og-primary, #4b3fff); color: #fff;
      border: 1px solid var(--og-primary, #4b3fff) !important;
      padding: 5px 14px; border-radius: var(--og-radius-sm, 6px);
    }
    .ct-dp-btn-save:hover { opacity: 0.9; }
  `],
  template: `
    <div #modalBackdrop class="ct-dp-backdrop"
      role="dialog" aria-modal="true"
      [attr.aria-labelledby]="'dp-title-' + rowId"
      (mousedown)="onBackdropClick($event)"
      (keydown)="onModalKeydown($event)">
      <div class="ct-dp-modal" (mousedown)="$event.stopPropagation()">

        <div class="ct-dp-modal-header">
          <span class="ct-dp-modal-title" [id]="'dp-title-' + rowId">{{ column.label }}</span>
          <button type="button" class="ct-dp-modal-close" (click)="cancelModal()" aria-label="Close">
            <lucide-angular name="x" [size]="14" aria-hidden="true"></lucide-angular>
          </button>
        </div>

        <div class="ct-dp-body">
          <div class="ct-dp-header">
            <button type="button" (click)="prevMonth()" aria-label="Previous month">
              <lucide-angular name="chevron-left" [size]="14" aria-hidden="true"></lucide-angular>
            </button>
            <span class="ct-dp-title">{{ monthNames[viewMonth] }} {{ viewYear }}</span>
            <button type="button" (click)="nextMonth()" aria-label="Next month">
              <lucide-angular name="chevron-right" [size]="14" aria-hidden="true"></lucide-angular>
            </button>
          </div>

          <div class="ct-dp-weekdays">
            <span *ngFor="let wd of weekdays" class="ct-dp-weekday">{{ wd }}</span>
          </div>

          <div #calendarGrid class="ct-dp-days" role="grid"
            [attr.aria-label]="monthNames[viewMonth] + ' ' + viewYear"
            (keydown)="onCalendarKeydown($event)">
            <button *ngFor="let day of calendarDays; let i = index"
              type="button" class="ct-dp-day" role="gridcell"
              [class.other-month]="!day.isCurrentMonth"
              [class.today]="isToday(day)"
              [class.selected]="isSelected(day)"
              [class.focused]="focusedDayIndex === i"
              [attr.aria-selected]="isSelected(day)"
              [attr.aria-label]="day.date + ' ' + monthNames[day.month] + ' ' + day.year"
              [attr.tabindex]="focusedDayIndex === i ? 0 : -1"
              (click)="selectDay(day, $event)"
              (focus)="focusedDayIndex = i">
              {{ day.date }}
            </button>
          </div>

          <div *ngIf="isDatetime" class="ct-dp-time">
            <label>Time</label>
            <input type="number" min="0" max="23" [value]="hours" (input)="onHoursChange($event)" aria-label="Hours" />
            <span class="ct-dp-time-sep">:</span>
            <input type="number" min="0" max="59" [value]="minutes" (input)="onMinutesChange($event)" aria-label="Minutes" />
          </div>
        </div>

        <div class="ct-dp-footer">
          <button type="button" class="ct-dp-btn-clear" (click)="clearDate($event)">Clear</button>
          <button type="button" class="ct-dp-btn-today" (click)="goToday($event)">Today</button>
          <span class="ct-dp-spacer"></span>
          <button type="button" class="ct-dp-btn-cancel" (click)="cancelModal()">Cancel</button>
          <button type="button" class="ct-dp-btn-save" (click)="saveModal()">Save</button>
        </div>
      </div>
    </div>
  `,
})
export class DateEditorComponent extends BaseCellEditor implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('modalBackdrop') modalBackdropRef!: ElementRef<HTMLElement>;
  @ViewChild('calendarGrid') calendarGrid!: ElementRef<HTMLElement>;

  weekdays = WEEKDAY_LABELS;
  monthNames = MONTH_NAMES;

  viewMonth = 0;
  viewYear = 2026;
  calendarDays: CalendarDay[] = [];
  focusedDayIndex = -1;

  hours = '12';
  minutes = '00';

  selectedIso = '';
  private portalEl: HTMLElement | null = null;

  get isDatetime(): boolean { return this.column.type === 'datetime'; }

  get displayValue(): string {
    if (!this.selectedIso) return '';
    try {
      const d = this.parseLocal(this.selectedIso);
      if (Number.isNaN(d.getTime())) return this.selectedIso;
      const datePart = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      if (!this.isDatetime) return datePart;
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      return `${datePart} ${h}:${m}`;
    } catch { return this.selectedIso; }
  }

  constructor(
    private readonly a11y: ChildTableA11yService,
    private readonly cdr: ChangeDetectorRef,
  ) { super(); }

  ngOnInit(): void {
    this.editingValue = this.value ?? '';
    this.selectedIso = this.editingValue ? String(this.editingValue) : '';

    const parsed = this.selectedIso ? this.parseLocal(this.selectedIso) : new Date();
    const ref = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    this.viewMonth = ref.getMonth();
    this.viewYear = ref.getFullYear();

    if (this.isDatetime && this.selectedIso) {
      const d = this.parseLocal(this.selectedIso);
      if (!Number.isNaN(d.getTime())) {
        this.hours = String(d.getHours()).padStart(2, '0');
        this.minutes = String(d.getMinutes()).padStart(2, '0');
      }
    }

    this.buildCalendar();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.modalBackdropRef?.nativeElement;
      if (el) {
        document.body.appendChild(el);
        this.portalEl = el;
        this.focusCalendarDay();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.portalEl?.parentNode === document.body) {
      document.body.removeChild(this.portalEl);
    }
  }

  // -- Calendar generation --

  buildCalendar(): void {
    const days: CalendarDay[] = [];
    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const startDay = firstOfMonth.getDay();

    const prevMonth = this.viewMonth === 0 ? 11 : this.viewMonth - 1;
    const prevYear = this.viewMonth === 0 ? this.viewYear - 1 : this.viewYear;
    const daysInPrev = new Date(prevYear, prevMonth + 1, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const d = daysInPrev - i;
      days.push({ date: d, month: prevMonth, year: prevYear, isCurrentMonth: false, iso: this.toIso(prevYear, prevMonth, d) });
    }

    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ date: d, month: this.viewMonth, year: this.viewYear, isCurrentMonth: true, iso: this.toIso(this.viewYear, this.viewMonth, d) });
    }

    const nextMonth = this.viewMonth === 11 ? 0 : this.viewMonth + 1;
    const nextYear = this.viewMonth === 11 ? this.viewYear + 1 : this.viewYear;
    let nextDay = 1;
    while (days.length < 42) {
      days.push({ date: nextDay, month: nextMonth, year: nextYear, isCurrentMonth: false, iso: this.toIso(nextYear, nextMonth, nextDay) });
      nextDay++;
    }

    this.calendarDays = days;

    if (this.selectedIso) {
      const idx = days.findIndex(d => d.iso === this.selectedIso.substring(0, 10));
      this.focusedDayIndex = idx >= 0 ? idx : days.findIndex(d => d.isCurrentMonth && d.date === 1);
    } else {
      this.focusedDayIndex = days.findIndex(d => d.isCurrentMonth && d.date === 1);
    }
  }

  private toIso(year: number, month: number, day: number): string {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  isToday(day: CalendarDay): boolean {
    const now = new Date();
    return day.date === now.getDate() && day.month === now.getMonth() && day.year === now.getFullYear();
  }

  isSelected(day: CalendarDay): boolean {
    return this.selectedIso.startsWith(day.iso);
  }

  // -- Navigation --

  prevMonth(): void {
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; }
    else { this.viewMonth--; }
    this.buildCalendar();
    this.cdr.markForCheck();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; }
    else { this.viewMonth++; }
    this.buildCalendar();
    this.cdr.markForCheck();
  }

  goToday(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const now = new Date();
    this.viewMonth = now.getMonth();
    this.viewYear = now.getFullYear();
    this.buildCalendar();
    const todayIso = this.toIso(now.getFullYear(), now.getMonth(), now.getDate());
    this.selectByIso(todayIso);
    this.cdr.markForCheck();
  }

  clearDate(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedIso = '';
    this.editingValue = '';
    this.save.emit('');
  }

  // -- Selection --

  selectDay(day: CalendarDay, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectByIso(day.iso);
    if (!this.isDatetime) {
      this.confirmAndClose();
    } else {
      setTimeout(() => {
        const hoursInput = this.portalEl?.querySelector('input[aria-label="Hours"]') as HTMLInputElement;
        if (hoursInput) {
          hoursInput.focus();
          hoursInput.select();
        }
      });
    }
    this.cdr.markForCheck();
  }

  private selectByIso(iso: string): void {
    if (this.isDatetime) {
      this.selectedIso = `${iso}T${this.hours.padStart(2, '0')}:${this.minutes.padStart(2, '0')}`;
    } else {
      this.selectedIso = iso;
    }
    this.editingValue = this.selectedIso;
    this.a11y.announce(`Selected ${this.displayValue}`);
  }

  confirmAndClose(): void {
    if (this.isDatetime && this.selectedIso) {
      const d = new Date(this.selectedIso);
      if (!Number.isNaN(d.getTime())) {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        this.editingValue = `${mm}/${dd}/${yyyy} ${hh}:${min}`;
      }
    }
    this.save.emit(this.editingValue);
  }

  // -- Modal actions --

  saveModal(): void { this.confirmAndClose(); }
  cancelModal(): void { this.cancel.emit(); }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('ct-dp-backdrop')) {
      this.saveModal();
    }
  }

  // -- Time inputs --

  onHoursChange(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.hours = String(Math.max(0, Math.min(23, Number(val) || 0))).padStart(2, '0');
    if (this.selectedIso) {
      const datePart = this.selectedIso.substring(0, 10);
      this.selectedIso = `${datePart}T${this.hours}:${this.minutes}`;
      this.editingValue = this.selectedIso;
    }
  }

  onMinutesChange(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.minutes = String(Math.max(0, Math.min(59, Number(val) || 0))).padStart(2, '0');
    if (this.selectedIso) {
      const datePart = this.selectedIso.substring(0, 10);
      this.selectedIso = `${datePart}T${this.hours}:${this.minutes}`;
      this.editingValue = this.selectedIso;
    }
  }

  // -- Keyboard --

  onModalKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelModal();
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  onCalendarKeydown(event: KeyboardEvent): void {
    const len = this.calendarDays.length;
    let idx = this.focusedDayIndex;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        idx = Math.min(idx + 1, len - 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        idx = Math.max(idx - 1, 0);
        break;
      case 'ArrowDown':
        event.preventDefault();
        idx = Math.min(idx + 7, len - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        idx = Math.max(idx - 7, 0);
        break;
      case 'Home':
        event.preventDefault();
        idx = this.calendarDays.findIndex(d => d.isCurrentMonth && d.date === 1);
        break;
      case 'End': {
        event.preventDefault();
        const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
        idx = this.calendarDays.findIndex(d => d.isCurrentMonth && d.date === daysInMonth);
        break;
      }
      case 'Enter': case ' ':
        event.preventDefault();
        event.stopPropagation();
        if (idx >= 0 && idx < len) {
          this.selectDay(this.calendarDays[idx]);
        }
        return;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.cancelModal();
        return;
      default:
        return;
    }

    this.focusedDayIndex = idx;
    this.focusCalendarDay();

    const day = this.calendarDays[idx];
    if (day && !day.isCurrentMonth) {
      this.viewMonth = day.month;
      this.viewYear = day.year;
      this.buildCalendar();
      const newIdx = this.calendarDays.findIndex(d => d.iso === day.iso);
      if (newIdx >= 0) this.focusedDayIndex = newIdx;
      this.cdr.markForCheck();
      setTimeout(() => this.focusCalendarDay());
    } else {
      this.cdr.markForCheck();
    }
  }

  private focusCalendarDay(): void {
    const el = this.portalEl ?? this.calendarGrid?.nativeElement?.closest('.ct-dp-modal');
    if (!el) return;
    const buttons = el.querySelectorAll('.ct-dp-day');
    const btn = buttons[this.focusedDayIndex] as HTMLElement;
    btn?.focus();
  }

  /** Parse a date string as local time, avoiding the UTC shift from `new Date("YYYY-MM-DD")`. */
  private parseLocal(s: string): Date {
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return new Date(s.includes('T') ? s : s + 'T00:00:00');
    }
    const parts = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(.*)$/);
    if (parts) {
      const d = new Date(+parts[3], +parts[1] - 1, +parts[2]);
      const t = parts[4]?.trim().match(/(\d{1,2}):(\d{2})/);
      if (t) {
        let h = +t[1];
        if (/pm/i.test(parts[4]) && h < 12) h += 12;
        if (/am/i.test(parts[4]) && h === 12) h = 0;
        d.setHours(h, +t[2]);
      }
      return d;
    }
    return new Date(s);
  }

  private trapFocus(event: KeyboardEvent): void {
    if (this.portalEl) {
      this.a11y.trapFocus(event, this.portalEl);
    }
  }
}
